import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Header from '../../components/common/Header';
import {
  getBetterPackagesForCart,
  getLabTestsCart,
  getOrderSummaryLabs,
  addPackageToCart,
  payLabCart,
  removeLabTestRecommended,
} from '../../services';
import LoadingComponent from '../../components/common/LoadingComponent';
import Modal from '../common/Modal';
import { FlyInTransition } from '../../components/transitions/transitions';
import StyledTappable from '../../components/common/StyledTappable';
import { FaTimes } from 'react-icons/fa';
import RecommendedPackageCard from '../../components/common/RecommendedPackageCard';

import ReportTat from '../../images/upsell/ReportTat.svg';
import ParameterPlaceholder from '../../images/upsell/Parameter.svg';
import UpgradeTestIcon from '../../images/upsell/upgrade-test.svg';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f2f2f3;
  position: relative;
`;

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 140px;
`;

const SectionTitle = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #100e15;
  margin-bottom: 12px;
`;

/* ─── Patient Card ─── */
const PatientSection = styled.div`
  padding: 16px;
`;

const PatientCard = styled.div`
  display: flex;
  align-items: flex-start;
  background: #fafafa;
  border: 1px solid #f0f3f6;
  border-radius: 12px;
  padding: 8px;
  position: relative;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border: 2px solid #714fff;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  margin-right: 12px;
  background: #f3f0ff;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PatientInfo = styled.div`
  flex: 1;
`;

const PatientName = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #100e15;
  margin: 0 0 4px;
  line-height: 140%;
`;

const SlotLabel = styled.p`
  font-size: 12px;
  color: #6d6b7c;
  font-weight: 500;
  line-height: 150%;
  margin: 0 0 2px;
`;

const SlotValue = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: #000;
  margin: 0;
`;

/* ─── Cart Section ─── */
const CartSection = styled.div`
  padding: 0 16px 16px;
`;

const CartItemCard = styled.div`
  background: #fff;
  border: 1px solid #f2f2f3;
  border-radius: 12px;
  position: relative;
  margin-bottom: 12px;
`;

const CartItemRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;

  background: ${(p) =>
    p.bg
      ? 'linear-gradient(277deg, #FEFBF5 -37.26%, #FDEDD6 161.23%)'
      : '#ffffff'};

  border-radius: ${(p) => (p.bg ? '12px 12px 0 0' : '0 0 12px 12px')};
`;

const LabLogo = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  margin-right: 12px;
  border: 1px solid #f0f0f0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const CartItemName = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #0f0b28;
  margin: 0;
  flex: 1;
`;

const PriceContainer = styled.div`
  text-align: right;
  margin-left: 12px;
`;

const DiscountedPrice = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #0f0b28;
  margin: 0;
`;

const OriginalPrice = styled.p`
  font-size: 12px;
  color: #767787;
  text-decoration: line-through;
  margin: 0;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fee2e2;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
`;

const CartBottomText = styled.p`
  font-size: 12px;
  font-weight: 500;
  color: #767787;
  text-align: center;
  margin: 0 0 4px;
`;

/* ─── Upgrade Banner ─── */
const UpgradeBanner = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  background: linear-gradient(90deg, var(--Primary-100, #ede8ff) 0%, #fff 100%);
  border: 1px solid #ede8ff;
  border-radius: 12px;
  padding: 8px 12px;
  margin: 0 16px 16px;
  cursor: pointer;
`;

const UpgradeIcon = styled.div`
  width: 24px;
  height: 24px;
  margin-right: 10px;
`;

const UpgradeTextWrap = styled.div`
  flex: 1;
`;

const UpgradeText = styled.p`
  color: #551ee3;
  font-family: Inter;
  font-size: 12px;
  font-weight: 600;
  line-height: 140%;
`;

const FreeBadge = styled.span`
  display: inline-block;
  background: linear-gradient(89deg, #714fff 0.36%, #442f99 107.17%);
  padding: 2px 8px;
  border-radius: 4px;
  color: #fff;
  font-family: Inter;
  font-size: 10px;
  font-style: italic;
  font-weight: 600;
  line-height: 12px;
`;

const ChevronRight = styled.span`
  font-size: 18px;
  color: #714fff;
  margin-left: 8px;
`;

/* ─── Pricing Section ─── */
const Divider = styled.div`
  height: 6px;
  background: #f5f5f5;
  margin: 8px 0;
`;

const PricingSection = styled.div`
  padding: 16px;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`;

const PriceLabel = styled.p`
  font-size: 14px;
  font-weight: ${(p) => (p.bold ? '700' : '400')};
  color: ${(p) => p.color || '#0f0b28'};
  margin: 0;
`;

const PriceValue = styled.p`
  font-size: 14px;
  font-weight: ${(p) => (p.bold ? '700' : '500')};
  color: ${(p) => p.color || '#0f0b28'};
  margin: 0;
  font-style: ${(p) => (p.italic ? 'italic' : 'normal')};
`;

const YouPayRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-top: 1px solid #eee;
`;

const YouPayLabel = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #0f0b28;
  margin: 0;
`;

const YouPayValue = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: #0f0b28;
  margin: 0;
`;

/* ─── Consent ─── */
const ConsentBox = styled.div`
  padding: 16px;
  margin: 0 0 8px;
`;

const ConsentText = styled.p`
  font-size: 12px;
  color: #767787;
  margin: 0;
  line-height: 1.6;
`;

/* ─── Home Collection Charge Banner ─── */
const HomeCollectionBanner = styled.div`
  text-align: center;
  padding: 10px 16px;
  background: #fff7f0;
  border: 1px dashed #ff8a65;
  border-radius: 8px;
  margin: 8px 16px 0;
`;

const HomeCollectionText = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #e65100;
  margin: 0;
`;

/* ─── Footer ─── */
const Footer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-width: 440px;
  margin: 0 auto;
  background: #fff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  z-index: 10;
`;

const FooterLeft = styled.div``;

const FooterLabel = styled.p`
  font-size: 12px;
  color: #767787;
  margin: 0 0 2px;
`;

const FooterAmount = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: #0f0b28;
  margin: 0;
`;

const BookNowButton = styled.button`
  background: #714fff;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  padding: 14px 32px;
  cursor: pointer;
`;

/* ─── Recommended Packages Modal ─── */
const CloseContainer = styled(StyledTappable)`
  width: 1.625rem;
  height: 1.625rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseIcon = styled(FaTimes)`
  color: #fff;
  font-size: 12px;
`;

const CardScrollContainer = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 0 16px 24px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ScrollIndicatorTrack = styled.div`
  width: 180px;
  height: 6px;
  margin: 4px auto 12px;
  border-radius: 999px;
  background: #d2d0dc;
  position: relative;
  overflow: hidden;
`;

const ScrollIndicatorThumb = styled.div`
  width: 36px;
  height: 100%;
  border-radius: 999px;
  background: #694ce1;
  transition: transform 0.12s linear;
`;

/* ─── Additional tests list (nested bottom sheet) ─── */
const AdditionalTestsSheet = styled.div`
  background: #fff;
  border-radius: 12px 12px 0 0;
  width: 100%;
  max-width: 440px;
  min-height: 35vh;
  max-height: 75vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const AdditionalTestsHeader = styled.div`
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f3f6;
  flex-shrink: 0;
`;

const AdditionalTestsTitle = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 21px;
  color: #100e15;
`;

const AdditionalTestsCloseWrap = styled(StyledTappable)`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ede8ff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AdditionalTestsCloseIcon = styled(FaTimes)`
  color: #714fff;
  font-size: 10px;
`;

const AdditionalTestsScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 0 24px;
`;

const AdditionalTestsRow = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f0f3f6;
  font-size: 14px;
  font-weight: 500;
  color: #121217;
  line-height: 1.45;

  &:last-child {
    border-bottom: none;
  }
`;

const CrossIcon = () => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
    <path
      d="M1 1l6 6M7 1L1 7"
      stroke="#EF4444"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const AvatarPlaceholder = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="8" r="4" fill="#714FFF" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" fill="#714FFF" />
  </svg>
);

export default function OrderSummary() {
  const history = useHistory();
  const location = useLocation();
  const authToken = useSelector((state) => state.user.authToken);

  const [loading, setLoading] = useState(true);
  const [cartSummary, setCartSummary] = useState(null);
  const [cartItemsResult, setCartItemsResult] = useState(null);
  const [cartItemsIds, setCartItemsIds] = useState([]);
  const [lab, setLab] = useState(null);
  const [consentBoxContent, setConsentBoxContent] = useState('');
  const [additionalTestDetails, setAdditionalTestDetails] = useState(null);
  const [showRecommendedPackage, setShowRecommendedPackage] = useState(false);
  const [viewTestsPackage, setViewTestsPackage] = useState(null);
  const [cardScrollProgress, setCardScrollProgress] = useState(0);
  const cardScrollRef = useRef(null);

  useMemo(() => additionalTestDetails, [additionalTestDetails]);

  const patientFromNav = location.state?.patient;
  const slotFromNav = location.state?.selectedSlot;

  const patientName = cartSummary?.patient || patientFromNav?.name || '';
  const patientIcon =
    cartSummary?.patientIcon || patientFromNav?.avatarUrl || '';
  const slotDetails =
    cartSummary?.slotDetails ||
    (slotFromNav ? `${slotFromNav.date}, ${slotFromNav.time}` : '');

  const itemsList = cartItemsResult?.itemsList || [];
  const labImage = cartItemsResult?.labImage || lab?.image || '';
  const paymentLine = cartItemsResult?.paymentLine || [];
  const payableByUser = cartItemsResult?.payableByUser ?? 0;
  const deliveryCost = cartItemsResult?.deliveryCost ?? 0;

  const visiblePaymentLines = paymentLine.filter((entry) => !entry.isHidden);
  const level1Lines = visiblePaymentLines.filter((e) => e.level === 1);
  const level2Lines = visiblePaymentLines.filter((e) => e.level === 2);
  const level3Lines = visiblePaymentLines.filter((e) => e.level === 3);

  const [betterUpgradeBanner, setBetterUpgradeBanner] = useState(null);
  const [recommendedPackages, setRecommendedPackages] = useState([]);
  const [upgradePackageLoadingId, setUpgradePackageLoadingId] = useState(null);

  const mapBetterPackageToCard = (pkg) => {
    const id = pkg?.packageId ?? pkg?.id;
    const name = pkg?.packageName ?? '';
    const description = pkg?.packageInvestigation;
    const offerPrice = pkg?.packageCost ?? 0;
    const mrp = pkg?.packageMrp ?? null;
    const additionalTestCount = pkg?.additionalTestCount ?? 0;
    const additionalTests = pkg?.additionalTests ?? [];
    const rawTests = pkg?.additionalTests || pkg?.coveredTests || [];
    const tests = Array.isArray(rawTests)
      ? rawTests
          .map((t) => ({
            name: t?.name || '',
            count: t?.testCount || 1,
          }))
          .filter((t) => t.name)
          .slice(0, 2)
      : [];
    const upgradeExtraCost = pkg?.upgradeCost ?? 0;
    const freeHomeCollection = pkg?.isFreeHomeCollection ?? false;
    const partnerImage = pkg?.partnerImageUrl ?? UpgradeTestIcon;

    return {
      id,
      name,
      partnerImage,
      description,
      additionalTestCount,
      additionalTests,
      tests,
      offerPrice,
      mrp,
      upgradeExtraCost,
      freeHomeCollection,
      _raw: pkg,
    };
  };

  const fetchOrderSummary = (overrideCartId) => {
    setLoading(true);
    const cartId = overrideCartId ?? location.state?.cartId;
    getOrderSummaryLabs(cartId, authToken)
      .then(async (res) => {
        if (res.message === 'success') {
          setCartSummary(res.cartSummary);
          setCartItemsResult(res.cartItemsResult);
          setCartItemsIds(
            res.cartItemsResult?.itemsList?.map((item) => item.cartItemId)
          );

          setLab(res.lab);
          setConsentBoxContent(res.consentBoxContent || '');

          const addOnDetails =
            res.additonalTestDetails || res.additionalTestDetails || null;
          setAdditionalTestDetails(addOnDetails);

          try {
            const { data: better } = await getBetterPackagesForCart(
              cartId,
              authToken
            );
            const pkgs = Array.isArray(better?.recommendedPackages)
              ? better.recommendedPackages
              : [];
            if (Array.isArray(pkgs) && pkgs.length > 0) {
              setRecommendedPackages(pkgs.map(mapBetterPackageToCard));
              const first = pkgs[0] || null;
              setBetterUpgradeBanner({
                additionalTestCount:
                  better?.additionalTestCount ??
                  first?.additionalTestCount ??
                  0,
                upgradeCost: better?.upgradeCost ?? first?.upgradeCost ?? 0,
                isFreeHomeCollection: first?.isFreeHomeCollection ?? false,
                message:
                  better?.recommendationMessage || 'Recommended Packages',
              });
            } else {
              setRecommendedPackages([]);
              setBetterUpgradeBanner(null);
              setShowRecommendedPackage(false);
              setViewTestsPackage(null);
            }
          } catch (e) {
            setRecommendedPackages([]);
            setBetterUpgradeBanner(null);
            setShowRecommendedPackage(false);
            setViewTestsPackage(null);
          }
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchOrderSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showRecommendedPackage && cardScrollRef.current) {
      cardScrollRef.current.scrollLeft = 0;
      setCardScrollProgress(0);
    }
  }, [showRecommendedPackage]);

  const handleRemoveItem = (cartItemId) => {
    removeLabTestRecommended(
      cartItemId,
      cartItemsResult.cartId,
      authToken,
      true
    )
      .then(() => {
        fetchOrderSummary();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleUpgrade = () => {
    setShowRecommendedPackage(true);
  };

  const handleCloseRecommended = () => {
    setShowRecommendedPackage(false);
    setViewTestsPackage(null);
  };

  const handleRecommendedScroll = (e) => {
    const { scrollLeft, scrollWidth, clientWidth } = e.currentTarget;
    const maxScrollable = scrollWidth - clientWidth;
    if (maxScrollable <= 0) {
      setCardScrollProgress(0);
      return;
    }
    setCardScrollProgress(scrollLeft / maxScrollable);
  };

  const handleUpgradePackage = (pkg) => {
    const packageId =
      pkg?.id ?? pkg?.packageId ?? pkg?._raw?.packageId ?? pkg?._raw?.id;
    if (!packageId) return;

    if (upgradePackageLoadingId) {
      return;
    }

    setUpgradePackageLoadingId(packageId);

    const cartIdFromSummary = cartItemsResult?.cartId;
    const packageCartItemsToRemove = (cartItemsResult?.itemsList || []).filter(
      (item) =>
        item?.cartItemId != null &&
        item?.packageId != null &&
        String(item.packageId) !== String(packageId)
    );

    const removePreviousPackages =
      cartIdFromSummary && packageCartItemsToRemove.length > 0
        ? packageCartItemsToRemove.reduce(
            (chain, item) =>
              chain.then(() =>
                removeLabTestRecommended(
                  item.cartItemId,
                  cartIdFromSummary,
                  authToken,
                  true
                )
              ),
            Promise.resolve()
          )
        : Promise.resolve();

    removePreviousPackages
      .then(() =>
        getLabTestsCart(authToken).then((res) => {
          const cartId = res?.cartId;
          if (!cartId) return null;
          const cartItems = Array.isArray(res?.cart) ? res.cart : [];

          const packageAlreadyInCart = cartItems.some(
            (item) =>
              item?.packageId != null &&
              String(item.packageId) === String(packageId)
          );

          const addIfNeeded = packageAlreadyInCart
            ? Promise.resolve()
            : addPackageToCart(cartId, packageId, authToken).catch((err) => {
                if (err?.response?.status === 400) return;
                throw err;
              });

          return addIfNeeded.then(() => cartId);
        })
      )
      .then((cartId) => {
        if (!cartId) return;
        setShowRecommendedPackage(false);
        setViewTestsPackage(null);
        fetchOrderSummary(cartId);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setUpgradePackageLoadingId(null);
      });
  };

  const handleViewTests = (pkg) => {
    setViewTestsPackage(pkg);
  };

  const handleCloseAdditionalTestsList = () => {
    setViewTestsPackage(null);
  };

  const handleBookNow = () => {
    if (!cartItemsResult) return;
    payLabCart(
      cartItemsResult.payableByUser,
      cartItemsResult.cartId,
      history,
      authToken
    );
  };

  const formatPrice = (val) => {
    const num = Number(val);
    if (isNaN(num)) return val;
    return num.toLocaleString('en-IN');
  };

  if (loading) {
    return (
      <Container>
        <Header title="Order Review" onTap={() => history.goBack()} noBorder />
        <LoadingComponent />
      </Container>
    );
  }

  return (
    <Container>
      <Header
        title="Order Review"
        onTap={() => {
          removeLabTestRecommended(
            '',
            cartItemsResult.cartId,
            authToken,
            true,
            cartItemsIds
          );
          history.push('/home');
        }}
      />

      <ScrollContent>
        <div style={{ backgroundColor: '#fff' }}>
          <PatientSection>
            <SectionTitle>Patient Name</SectionTitle>
            <PatientCard>
              <Avatar>
                {patientIcon ? (
                  <img src={patientIcon} alt={patientName} />
                ) : (
                  <AvatarPlaceholder />
                )}
              </Avatar>
              <PatientInfo>
                <PatientName>{patientName}</PatientName>
                <SlotLabel>Date & Time Slot</SlotLabel>
                <SlotValue>{slotDetails}</SlotValue>
              </PatientInfo>
            </PatientCard>
          </PatientSection>

          <CartSection>
            <SectionTitle>Your Cart</SectionTitle>
            {itemsList.map((item, idx) => (
              <CartItemCard key={item.cartItemId}>
                {idx !== 0 && (
                  <RemoveButton
                    onClick={() => handleRemoveItem(item.cartItemId)}
                  >
                    <CrossIcon />
                  </RemoveButton>
                )}
                <CartItemRow bg={true}>
                  <LabLogo>
                    <img src={labImage} alt={lab?.name || 'lab'} />
                  </LabLogo>
                  <CartItemName>{item.name}</CartItemName>
                  <PriceContainer>
                    <DiscountedPrice>
                      ₹{formatPrice(item.postDiscountPrice)}
                    </DiscountedPrice>
                    {Number(item.mrp) !== item.postDiscountPrice && (
                      <OriginalPrice>₹{formatPrice(item.mrp)}</OriginalPrice>
                    )}
                  </PriceContainer>
                </CartItemRow>
                <CartItemRow bg={false} className="gap-3">
                  <div className="flex items-center gap-4px">
                    <img src={ReportTat} alt="lab" />
                    <div className="text-body-s">Reports in 12 hrs</div>
                  </div>
                </CartItemRow>
              </CartItemCard>
            ))}

            {cartItemsResult?.bottomCartText && (
              <CartBottomText>{cartItemsResult.bottomCartText}</CartBottomText>
            )}
          </CartSection>

          {recommendedPackages?.length > 0 && (
            <UpgradeBanner onClick={handleUpgrade}>
              <UpgradeIcon>
                <img
                  src={UpgradeTestIcon}
                  alt="upgrade"
                  style={{ width: '24px', height: '24px' }}
                />
              </UpgradeIcon>
              <UpgradeTextWrap>
                <UpgradeText>
                  Upgrade &amp; get {betterUpgradeBanner?.additionalTestCount}{' '}
                  more tests at ₹{formatPrice(betterUpgradeBanner?.upgradeCost)}
                </UpgradeText>
                {betterUpgradeBanner?.isFreeHomeCollection ||
                deliveryCost === 0 ? (
                  <FreeBadge>₹0 sample collection</FreeBadge>
                ) : null}
              </UpgradeTextWrap>
              <ChevronRight>&rsaquo;</ChevronRight>
            </UpgradeBanner>
          )}

          <Divider />

          <PricingSection>
            {level1Lines.map((entry, i) => (
              <PriceRow key={`l1-${i}`}>
                <PriceLabel style={{ color: entry.color }}>
                  {entry.label}
                </PriceLabel>
                <PriceValue style={{ color: entry.color }}>
                  {entry.value === 0 ? 'Free' : `₹${formatPrice(entry.value)}`}
                </PriceValue>
              </PriceRow>
            ))}

            {paymentLine
              .filter(
                (e) =>
                  e.level === 1 &&
                  e.isHidden &&
                  e.label.toLowerCase().includes('pickup')
              )
              .map((entry, i) => (
                <PriceRow key={`pickup-${i}`}>
                  <PriceLabel>{entry.label}</PriceLabel>
                  <PriceValue>Free</PriceValue>
                </PriceRow>
              ))}

            {level2Lines.map((entry, i) => (
              <PriceRow key={`l2-${i}`}>
                <PriceLabel style={{ color: entry.color }}>
                  {entry.label}
                </PriceLabel>
                <PriceValue style={{ color: entry.color }}>
                  {entry.type === 'negative' ? '-' : ''}₹
                  {formatPrice(entry.value)}
                </PriceValue>
              </PriceRow>
            ))}

            {level3Lines.map((entry, i) => (
              <YouPayRow key={`l3-${i}`}>
                <YouPayLabel style={{ color: entry.color }}>
                  {entry.label}
                </YouPayLabel>
                <YouPayValue style={{ color: entry.color }}>
                  ₹{formatPrice(entry.value)}
                </YouPayValue>
              </YouPayRow>
            ))}
          </PricingSection>
        </div>

        {consentBoxContent && (
          <ConsentBox>
            <ConsentText>{consentBoxContent}</ConsentText>
          </ConsentBox>
        )}

        {!cartSummary?.isCenterVisit && deliveryCost > 0 && (
          <HomeCollectionBanner>
            <HomeCollectionText>
              ₹{formatPrice(deliveryCost)} will be charged for Home sample
              collection
            </HomeCollectionText>
          </HomeCollectionBanner>
        )}
      </ScrollContent>

      <Footer>
        <FooterLeft>
          <FooterLabel>Total Amount</FooterLabel>
          <FooterAmount>₹ {formatPrice(payableByUser)}</FooterAmount>
        </FooterLeft>
        <BookNowButton onClick={handleBookNow}>Book Now</BookNowButton>
      </Footer>

      {showRecommendedPackage && (
        <Modal>
          <div className="modalContainer" onClick={handleCloseRecommended}>
            <FlyInTransition
              in={showRecommendedPackage}
              mountOnEnter
              unmountOnExit
              appear
            >
              <div
                className="modalBody"
                onClick={(e) => e.stopPropagation()}
                style={{
                  background:
                    'linear-gradient(180deg, var(--Color-Border-Brand-Intense, #551EE3) -16.93%, #FFF 100%)',
                  minHeight: '60vh',
                  maxHeight: '85vh',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <div
                  className="modalHeader"
                  style={{
                    borderBottom: 'none',
                    borderRadius: '12px 12px 0 0',
                  }}
                >
                  <p
                    className="text-body-l font-semibold"
                    style={{ color: '#fff', margin: 0 }}
                  >
                    Recommended Packages
                  </p>
                  <CloseContainer onTap={handleCloseRecommended}>
                    <CloseIcon />
                  </CloseContainer>
                </div>
                <div
                  className="modalDetails py-0 pl-16px pr-0"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'hidden',
                    maxHeight: 'none',
                    paddingBottom: 16,
                  }}
                >
                  <CardScrollContainer
                    ref={cardScrollRef}
                    onScroll={handleRecommendedScroll}
                  >
                    {recommendedPackages.map((pkg) => (
                      <RecommendedPackageCard
                        key={pkg.id}
                        name={pkg.name}
                        partnerImage={pkg.partnerImage}
                        description={pkg.description}
                        additionalTests={pkg.additionalTests}
                        additionalTestCount={pkg.additionalTestCount}
                        tests={pkg.tests}
                        offerPrice={pkg.offerPrice}
                        mrp={pkg.mrp}
                        upgradeExtraCost={pkg.upgradeExtraCost}
                        freeHomeCollection={pkg.freeHomeCollection}
                        onUpgrade={() => handleUpgradePackage(pkg)}
                        onViewTests={() => handleViewTests(pkg)}
                        cartTestCount={recommendedPackages.length}
                      />
                    ))}
                  </CardScrollContainer>
                  <ScrollIndicatorTrack>
                    <ScrollIndicatorThumb
                      style={{
                        transform: `translateX(${cardScrollProgress * (180 - 36)}px)`,
                      }}
                    />
                  </ScrollIndicatorTrack>
                </div>
              </div>
            </FlyInTransition>
          </div>
        </Modal>
      )}

      {viewTestsPackage && (
        <Modal>
          <div
            className="modalContainer"
            onClick={handleCloseAdditionalTestsList}
          >
            <FlyInTransition
              in={!!viewTestsPackage}
              mountOnEnter
              unmountOnExit
              appear
            >
              <AdditionalTestsSheet
                className="modalBody"
                onClick={(e) => e.stopPropagation()}
              >
                <AdditionalTestsHeader>
                  <AdditionalTestsTitle>
                    {(() => {
                      const list = Array.isArray(
                        viewTestsPackage.additionalTests
                      )
                        ? viewTestsPackage.additionalTests
                        : [];
                      const n =
                        list.length ||
                        viewTestsPackage.additionalTestCount ||
                        0;
                      return `${n} Test${n === 1 ? '' : 's'} Included`;
                    })()}
                  </AdditionalTestsTitle>
                  <AdditionalTestsCloseWrap
                    onTap={handleCloseAdditionalTestsList}
                  >
                    <AdditionalTestsCloseIcon />
                  </AdditionalTestsCloseWrap>
                </AdditionalTestsHeader>
                <AdditionalTestsScroll>
                  {(Array.isArray(viewTestsPackage.additionalTests)
                    ? viewTestsPackage.additionalTests
                    : []
                  ).map((t, idx) => (
                    <AdditionalTestsRow key={t?.id ?? idx}>
                      {t?.name || ''}
                    </AdditionalTestsRow>
                  ))}
                </AdditionalTestsScroll>
              </AdditionalTestsSheet>
            </FlyInTransition>
          </div>
        </Modal>
      )}
    </Container>
  );
}
